import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { errorHandler } from '$store/utils';
import { userLanguage } from '$translations/index';
import { ErrorMessageEn, ErrorMessageEs } from '$constants/alerts';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { tracker } from '$utils/tracker';

import InternalDashboardPages from '../constants/InternalDashboardPages';
import {
  SIMERA_CLIENT_ROUTE,
  SIMERA_SMART_INTERVIEWS,
  SIMERA_ADD_POSITION_ROUTE,
} from '../constants/routes';
import { useAppSelector } from '../hooks';
import RouteTypes from '../types/routeTypes';
import AuthRoute from './AuthRoute/AuthRoute';
import ContainerCandidateDetails from './CandidateDetails/ContainterCandidateDetails';
import EmailSubscription from './EmailSubscriptions/EmailSubscription';
import EmployeesList from './EmployerDashboard/EmployeesList/EmployeesList';
import EmployerLayout from './EmployerDashboard/EmployerLayout';
import LandingRedirect from './LandingRedirect/LandingRedirect';
import Policy from './Policy/Policy';
import PositionPageLayout from './PositionPageLayout/PositionPageLayout';
import { AdditionalVideosEmployerScreen } from './Screens/AdditionalVideosScreen/AdditionalVideosEmployer/AdditionalVideosEmployerScreen';
import AdditionalVideosInternalScreen from './Screens/AdditionalVideosScreen/AdditionalVideosInternal/AdditionalVideosInternal';
import CandidateNotesScreen from './Screens/CandidateNotesScreen/CandidateNotesScreen';
import ConfirmEmailScreen from './Screens/ConfirmEmail/ConfirmEmailScreen';
import EmployerRegisterScreen from './Screens/EmployerRegisterScreen/EmployerRegisterScreen';
import SimeraRegisterScreen from './Screens/EmployerRegisterScreen/SimeraRegisterScreen';
import EmployersScreen from './Screens/EmployersScreen/EmployersScreen';
import ForgotPasswordScreen from './Screens/ForgotPassword/ForgotPasswordScreen';
import HandleHomePagePrevius from './Screens/HandleHomePage/HandleHomePagePrevius';
import JobDashboardScreen from './Screens/JobDashboardScreen/JobDashboardScreen';
import SimeraLoginScreen from './Screens/LoginScreen/SimeraLoginScreen';
import PasswordResetScreen from './Screens/PasswordResetScreen/PasswordResetScreen';
import PasswordResetSuccess from './Screens/PasswordResetSuccess/PasswordResetSuccess';
import ReferralsScreen from './Screens/ReferralsScreen/ReferralsScreen';
import SimeraDashboardScreen from './Screens/SimeraDashboardScreen/SimeraDashboardScreen';
import SimeraDashboardScreenCandidates from './Screens/SimeraDashboardScreen/SimeraDashboardScreenCandidates';
import SimeraDashboardScreenCompanies from './Screens/SimeraDashboardScreen/SimeraDashboardScreenCompanies';
import SimeraDashboardScreenCompany from './Screens/SimeraDashboardScreen/SimeraDashboardScreenCompany';
import SimeraDashboardScreenPosition from './Screens/SimeraDashboardScreen/SimeraDashboardScreenPosition';
import SimeraDashboardLayout from './SimeraDashboard/SimeraDashboardLayout';
import Spinner from './Spinner/Spinner';
import Terms from './Terms/Terms';
import ShareVideo from './Share/ShareVideo';
import ShareLayout from './Share/ShareLayout';
import SharePosition from './Share/SharePosition/SharePosition';
import HomeLayout from './CandidateHome/HomeLayout';
import CandidateInterviewLayout from './Interview/CandidateInterviewLayout';
import InternalJobInterviewLayout from './InternalJobInterview/InternalJobInterviewLayout';
import PreviewFile from './PreviewFile/PreviewFile';
import SearchCandidates from './SearchCandidates/SearchCandidates';
import ShareCandidates from './Share/ShareCandidates/ShareCandidates';
import SPKShareLayout from './Share/SPKShareLayout';
import EmployerLoginScreen from './Screens/LoginScreen/EmployerLoginScreen';
import EmployerSPKLayout, {
  ActiveEmployerLayoutPageType,
} from './EmployerDashboard/EmployerSPKLayout';
import SPKCandidatesPositionPage from './Positions/SPKCandidatesPositionPage/SPKCandidatesPositionPage';
import { SmartInterviews } from './SmartInterviews/SmartInterviews';
import Redirect from './Redirect/Redirect';
import SPKAddPosition from './SPKAddPosition/SPKAddPosition';
import EmployerForgotPasswordScreen from './Screens/EmployerForgotPassword/EmployerForgotPasswordScreen';
import NavigateWithParams from './NavigateWithParams/NavigateWithParams';
import SsoLoginScreen from './Screens/SsoLoginScreen/SsoLoginScreen';

const LazyLoadedVideoScreen = lazy(
  () => import('./Screens/VideoRecordingScreen/VideoRecordingScreen')
);

interface CustomSwitchProps {}

const CustomSwitch: FC<CustomSwitchProps> = () => {
  const reCaptchaSiteKey: string =
    import.meta.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
  const location = useLocation();
  const authState = useAppSelector((state) => state.auth);
  const [referrer, setReferrer] = useState(document.referrer);
  const hasQuerystring = location.search !== '';

  useEffect(() => {
    if (
      (authState.isLoggedIn === true && referrer !== window.location.href) ||
      document.referrer !== '' ||
      location.pathname.includes('/share/')
    ) {
      tracker.viewPage({
        referrer: document.referrer !== '' ? document.referrer : referrer,
        url: window.location.href,
      });
    }
    setReferrer(window.location.href);
  }, [location.pathname, referrer, authState]);

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired');
      errorHandler(
        '[SessionExpired]',
        userLanguage === 'es'
          ? ErrorMessageEs.SessionExpired
          : ErrorMessageEn.SessionExpired,
        new Error('Session expired'),
        true
      );
    }
  }, []);

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        {/* public routes */}
        <Route
          path="/"
          element={
            hasQuerystring ? (
              <LandingRedirect />
            ) : (
              <Navigate to="/client_login" />
            )
          }
        />
        <Route element={<LandingRedirect />} path="/landing" />

        <Route
          path="/interview"
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <LazyLoadedVideoScreen />
            </AuthRoute>
          }
        />
        <Route
          path="/video"
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <LazyLoadedVideoScreen />
            </AuthRoute>
          }
        />

        <Route
          path="/job-dashboard"
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <JobDashboardScreen />
            </AuthRoute>
          }
        />

        <Route
          path="/job-dashboard/:positionId"
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <JobDashboardScreen />
            </AuthRoute>
          }
        />

        <Route
          path="/internal/video/:candidateId"
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.CANDIDATES}
              >
                <AdditionalVideosInternalScreen />
              </SimeraDashboardLayout>
            </AuthRoute>
          }
        />

        <Route
          path="/employer/video/:candidateId"
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <AdditionalVideosEmployerScreen />
            </AuthRoute>
          }
        />

        <Route path="/recover_password" element={<ForgotPasswordScreen />} />
        <Route
          path="/employer_recover_password"
          element={<EmployerForgotPasswordScreen />}
        />
        <Route path="/reset_password" element={<PasswordResetScreen />} />
        <Route path="/reset_success" element={<PasswordResetSuccess />} />

        <Route path="/terms" element={<Terms />} />
        <Route path="/policy" element={<Policy />} />

        {/* login routes */}
        {/* type redirect means that it will redirect to the default auth route if user is already logged in */}
        <Route
          path="/login"
          element={
            // Redirect to SPK login page
            <Redirect to={`${import.meta.env.REACT_APP_SPK_URL}/auth/signin`} />
          }
        />

        <Route
          path="/register"
          element={
            // Redirect to SPK register page
            <Redirect to={`${import.meta.env.REACT_APP_SPK_URL}/auth/signup`} />
          }
        />
        <Route
          path="/client_register"
          element={
            <AuthRoute type={RouteTypes.AUTH}>
              {reCaptchaSiteKey ? (
                <GoogleReCaptchaProvider
                  reCaptchaKey={reCaptchaSiteKey}
                  useEnterprise
                >
                  <EmployerRegisterScreen />
                </GoogleReCaptchaProvider>
              ) : (
                <EmployerRegisterScreen />
              )}
            </AuthRoute>
          }
        />
        <Route
          path="/employer_register"
          element={<NavigateWithParams to="/client_register" />}
        />
        <Route
          path="/internal_register"
          element={
            <AuthRoute type={RouteTypes.AUTH}>
              {reCaptchaSiteKey ? (
                <GoogleReCaptchaProvider
                  reCaptchaKey={reCaptchaSiteKey}
                  useEnterprise
                >
                  <SimeraRegisterScreen />
                </GoogleReCaptchaProvider>
              ) : (
                <SimeraRegisterScreen />
              )}
            </AuthRoute>
          }
        />

        <Route
          path="/client_login"
          element={
            <AuthRoute type={RouteTypes.AUTH}>
              <EmployerLoginScreen />
            </AuthRoute>
          }
        />
        <Route
          path="/employer_login"
          element={<NavigateWithParams to="/client_login" />}
        />

        <Route
          path="/internal_login"
          element={
            <AuthRoute type={RouteTypes.AUTH}>
              <SimeraLoginScreen />
            </AuthRoute>
          }
        />

        <Route path="/sso_login" element={<SsoLoginScreen />} />

        {/* private routes */}
        <Route
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <HandleHomePagePrevius />
            </AuthRoute>
          }
          path="/home"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <HandleHomePagePrevius forceProfile />
            </AuthRoute>
          }
          path="/profile"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardScreen />
            </AuthRoute>
          }
          path="/internal-dashboard"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardScreenCompany />
            </AuthRoute>
          }
          path="/internal-dashboard/company/:id"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardScreenCandidates />
            </AuthRoute>
          }
          path="/internal-dashboard/candidates"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardScreenCompanies />
            </AuthRoute>
          }
          path={SIMERA_CLIENT_ROUTE}
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.SMART_INTERVIEWS}
              >
                <SmartInterviews />
              </SimeraDashboardLayout>
            </AuthRoute>
          }
          path={SIMERA_SMART_INTERVIEWS}
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <ReferralsScreen />
            </AuthRoute>
          }
          path="/referrals"
        />

        <Route
          path="/internal/confirm_email/:token"
          element={<ConfirmEmailScreen userRole="internal" />}
        />

        <Route
          path="/employer/confirm_email/:token"
          element={<ConfirmEmailScreen userRole="employer" />}
        />

        <Route
          path="/candidate/confirm_email/:token"
          element={<ConfirmEmailScreen userRole="candidate" />}
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardScreenPosition />
            </AuthRoute>
          }
          path="/simera-dashboard/positions/:positionId"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <CandidateNotesScreen />
            </AuthRoute>
          }
          path="/simera-dashboard/candidate/:candidateId/notes/:positionId"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <EmployersScreen />
            </AuthRoute>
          }
          path="/simera-dashboard/employers"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.POSITION}
              >
                <div
                  style={{
                    width: '100%',
                    position: 'absolute',
                  }}
                >
                  <ContainerCandidateDetails secondary />
                </div>
              </SimeraDashboardLayout>
            </AuthRoute>
          }
          path="/simera-dashboard/positions/:positionId/candidates/:candidateId"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.POSITION}
              >
                <div
                  style={{
                    width: '100%',
                    position: 'absolute',
                  }}
                >
                  <ContainerCandidateDetails secondary />
                </div>
              </SimeraDashboardLayout>
            </AuthRoute>
          }
          path="/simera-dashboard/candidates/:candidateId"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.SEARCH_CANDIDATES}
              >
                <div
                  style={{
                    width: '100%',
                    position: 'absolute',
                  }}
                >
                  <SearchCandidates />
                </div>
              </SimeraDashboardLayout>
            </AuthRoute>
          }
          path="/simera-dashboard/search-candidates"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <EmployerLayout isProfile>
                <ContainerCandidateDetails isEmployee />
              </EmployerLayout>
            </AuthRoute>
          }
          path="/employer-dashboard/positions/:positionId/candidates/:candidateId"
        />
        <Route
          element={<Navigate to="/employer-dashboard/team-members/" replace />}
          path="/employer-dashboard/positions/:positionId/employees/:candidateId"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <PositionPageLayout />
            </AuthRoute>
          }
          path="/employer-dashboard/positions"
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <EmployerSPKLayout
                activePage={ActiveEmployerLayoutPageType.positions}
              >
                <SPKCandidatesPositionPage />
              </EmployerSPKLayout>
            </AuthRoute>
          }
          path="/employer-dashboard/positions/:positionId"
        />

        {/* Keeping this in case somewhere external is used */}
        <Route
          element={<Navigate to="/employer-dashboard/team-members/" replace />}
          path="/employer-dashboard/employees/"
        />
        {/* ***** */}
        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <EmployerSPKLayout
                activePage={ActiveEmployerLayoutPageType.teamMembers}
              >
                <EmployeesList />
              </EmployerSPKLayout>
            </AuthRoute>
          }
          path="/employer-dashboard/team-members/"
        />

        <Route
          path="/employer-dashboard/add-position"
          element={
            <AuthRoute type={RouteTypes.EMPLOYER}>
              <EmployerSPKLayout
                activePage={ActiveEmployerLayoutPageType.addPosition}
              >
                <SPKAddPosition />
              </EmployerSPKLayout>
            </AuthRoute>
          }
        />

        <Route
          path={SIMERA_ADD_POSITION_ROUTE}
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.POSITIONS}
              >
                <SPKAddPosition isInternal />
              </SimeraDashboardLayout>
            </AuthRoute>
          }
        />

        <Route
          path="/employer-dashboard"
          element={<Navigate to="/employer-dashboard/positions" replace />}
        />

        <Route path="/unsubscribe/:token" element={<EmailSubscription />} />

        {/* Keeping for share video url backward compability */}
        <Route
          path="/share/video/:shareReferenceId"
          element={
            <ShareLayout>
              <ShareVideo />
            </ShareLayout>
          }
        />
        <Route
          path="/share/candidate/:candidateId/video/:shareReferenceId"
          element={
            <ShareLayout>
              <ShareVideo />
            </ShareLayout>
          }
        />

        <Route
          path="/share/position/:shareReferenceId"
          element={
            <SPKShareLayout>
              <SharePosition />
            </SPKShareLayout>
          }
        />

        <Route
          path="/share/candidates/:shareCandidatesIds"
          element={
            <SPKShareLayout>
              <ShareCandidates />
            </SPKShareLayout>
          }
        />

        <Route
          path="/job-interview"
          element={
            <AuthRoute type={RouteTypes.CANDIDATE}>
              <HomeLayout isProfile>
                <CandidateInterviewLayout />
              </HomeLayout>
            </AuthRoute>
          }
        />

        <Route
          element={
            <AuthRoute type={RouteTypes.INTERNAL}>
              <SimeraDashboardLayout
                activeTab={InternalDashboardPages.JOBINTERVIEW}
              >
                <InternalJobInterviewLayout />
              </SimeraDashboardLayout>
            </AuthRoute>
          }
          path="/internal-dashboard/interviews"
        />

        {/* File Viewer */}
        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER_OR_INTERNAL}>
              <PreviewFile />
            </AuthRoute>
          }
          path="/file-viewer/:type/:candidateId/:fileId"
        />
        <Route
          element={
            <AuthRoute type={RouteTypes.EMPLOYER_OR_INTERNAL}>
              <PreviewFile />
            </AuthRoute>
          }
          path="/file-viewer/:type/:candidateId"
        />
      </Routes>
    </Suspense>
  );
};

export default CustomSwitch;
